import { useThemeStore } from '@liniya/client-core';
import { useEffect } from 'react';

/**
 * Keep the document and the shared theme store in step.
 *
 * The resolved theme — never the choice — is stamped on the root element, so
 * the stylesheet needs no media query of its own and "as the system does" is
 * settled in one place for both clients.
 */
export function useThemeBridge(): void {
  const resolved = useThemeStore((state) => state.resolved);

  useEffect(() => {
    // The store is built when its module loads, which may be before the
    // key-value store this client provides is registered; asking again is what
    // makes the choice survive a reload.
    useThemeStore.getState().hydrate();

    const query = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
    const apply = () =>
      useThemeStore.getState().setSystemTheme(query?.matches ? 'dark' : 'light');

    apply();
    query?.addEventListener('change', apply);
    return () => query?.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = resolved;
    // The browser paints scrollbars and form controls from this, and the
    // address bar takes its colour from the meta tag below.
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) meta.content = resolved === 'dark' ? '#171715' : '#fafaf8';
  }, [resolved]);
}
